import { fetchNearbyTemplates, publishTemplate, voteTemplate, searchCommunityStores, deleteCommunityTemplate } from '../api/community.js';
import { getAllStores } from '../db/stores.js';
import { getAislesByStore } from '../db/aisles.js';
import { addStore } from '../db/stores.js';
import { addAisle } from '../db/aisles.js';
import { searchAddressDebounced } from '../utils/geocode.js';
import { isLoggedIn, getUser } from '../utils/auth.js';
import { renderHeader } from '../components/header.js';
import { openModal, closeModal } from '../components/modal.js';
import { createElement, clearElement, showToast } from '../utils/dom.js';
import { navigate } from '../router.js';
import {
  iconGlobe,
  iconThumbsUp,
  iconUpload,
  iconDownload,
  iconMapPin,
  iconNavigation,
  iconStore,
  iconTrash,
} from '../utils/icons.js';

let userLat = null;
let userLng = null;

/**
 * Retourne le device_id : "user:{id}" si connecté, sinon UUID local.
 * @returns {string}
 */
function getDeviceId() {
  if (isLoggedIn()) {
    const user = getUser();
    if (user?.id) return `user:${user.id}`;
  }
  const key = 'grocewise-device-id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, id);
  }
  return id;
}

/**
 * Affiche la page communautaire.
 * @param {HTMLElement} container
 */
let leafletMap = null;
let mapMarkers = [];

export async function renderCommunityPage(container) {
  clearElement(container);
  leafletMap = null;
  mapMarkers = [];

  const page = createElement('div', { className: 'page page-community page--with-nav' });

  page.appendChild(renderHeader({
    title: 'Communauté',
    backPath: '/',
    actions: [{
      icon: iconUpload(20),
      label: 'Publier',
      onClick: () => openPublishModal(() => loadTemplates(listContainer)),
    }],
  }));

  // Carte
  const mapContainer = createElement('div', {
    className: 'community__map',
    attrs: { id: 'community-map' },
  });
  page.appendChild(mapContainer);

  // Liste des templates
  const listContainer = createElement('div', { className: 'community__list' });
  page.appendChild(listContainer);

  container.appendChild(page);

  // Géolocalisation
  listContainer.innerHTML = '<p class="text-muted text-center" style="padding:var(--space-xl)">📍 Localisation en cours…</p>';
  await getUserPosition();

  // Initialiser la carte après que le conteneur est dans le DOM
  requestAnimationFrame(() => initMap(mapContainer));

  await loadTemplates(listContainer);
}

/**
 * Initialise la carte Leaflet de manière robuste.
 */
function initMap(mapContainer) {
  if (leafletMap || typeof L === 'undefined') return;

  leafletMap = L.map(mapContainer, {
    zoomControl: false,
    attributionControl: false,
  }).setView([userLat || 48.8566, userLng || 2.3522], 13);

  L.control.zoom({ position: 'topright' }).addTo(leafletMap);

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 18,
  }).addTo(leafletMap);

  // Position actuelle
  L.circleMarker([userLat || 48.8566, userLng || 2.3522], {
    radius: 8,
    fillColor: '#4CAF78',
    color: '#fff',
    weight: 3,
    fillOpacity: 1,
  }).addTo(leafletMap).bindPopup('📍 Vous êtes ici');

  // Fix Leaflet resize — forcer le recalcul après le rendu
  setTimeout(() => leafletMap?.invalidateSize(), 100);
  setTimeout(() => leafletMap?.invalidateSize(), 500);
}

/**
 * Met à jour les markers sur la carte.
 */
function updateMapMarkers(templates) {
  if (!leafletMap || typeof L === 'undefined') return;

  for (const m of mapMarkers) leafletMap.removeLayer(m);
  mapMarkers = [];

  for (const tpl of templates) {
    const marker = L.marker([tpl.store.lat, tpl.store.lng])
      .addTo(leafletMap)
      .bindPopup(
        `<strong>${tpl.store.name}</strong><br>` +
        `${tpl.aisles.length} rayons • 👍 ${tpl.vote_count}<br>` +
        `<em>par ${tpl.pseudo}</em>`
      );
    mapMarkers.push(marker);
  }
}

/**
 * Demande la position GPS de l'utilisateur.
 */
function getUserPosition() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      // Fallback : Paris
      userLat = 48.8566;
      userLng = 2.3522;
      resolve();
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        userLat = pos.coords.latitude;
        userLng = pos.coords.longitude;
        resolve();
      },
      () => {
        userLat = 48.8566;
        userLng = 2.3522;
        showToast('Position non disponible, Paris par défaut', 3000, 'error');
        resolve();
      },
      { enableHighAccuracy: false, timeout: 8000 },
    );
  });
}

/**
 * Charge et affiche les templates.
 */
async function loadTemplates(listContainer) {
  clearElement(listContainer);

  try {
    const templates = await fetchNearbyTemplates({
      lat: userLat,
      lng: userLng,
      radiusKm: 50,
      deviceId: getDeviceId(),
    });

    if (templates.length === 0) {
      listContainer.appendChild(createElement('div', {
        className: 'empty-state',
        children: [
          createElement('span', { className: 'empty-state__icon', children: [iconGlobe(48)] }),
          createElement('h2', { className: 'empty-state__title', text: 'Aucun template' }),
          createElement('p', {
            className: 'empty-state__message',
            text: 'Soyez le premier à publier un template pour votre magasin !',
          }),
        ],
      }));
    } else {
      for (const tpl of templates) {
        listContainer.appendChild(renderTemplateCard(tpl, listContainer));
      }
      updateMapMarkers(templates);
    }
  } catch (err) {
    listContainer.appendChild(createElement('p', {
      className: 'text-muted text-center',
      text: err.message || 'Erreur de chargement',
    }));
  }
}

/**
 * Rend une carte template.
 */
function renderTemplateCard(tpl, listContainer) {
  const aisleNames = tpl.aisles.map((a) => a.name).join(' • ');
  const distance = getDistanceKm(userLat, userLng, tpl.store.lat, tpl.store.lng);
  const distText = distance < 1 ? `${Math.round(distance * 1000)}m` : `${distance.toFixed(1)}km`;

  const voteBtn = createElement('button', {
    className: `community__vote-btn ${tpl.user_voted ? 'community__vote-btn--active' : ''}`,
    attrs: { type: 'button' },
    children: [
      iconThumbsUp(16),
      createElement('span', { text: ` ${tpl.vote_count}` }),
    ],
    events: {
      click: async (e) => {
        e.stopPropagation();
        try {
          const res = await voteTemplate(tpl.id, getDeviceId());
          tpl.user_voted = res.voted;
          tpl.vote_count += res.voted ? 1 : -1;
          voteBtn.classList.toggle('community__vote-btn--active', res.voted);
          voteBtn.querySelector('span').textContent = ` ${tpl.vote_count}`;
        } catch {
          showToast('Erreur de vote', 3000, 'error');
        }
      },
    },
  });

  const importBtn = createElement('button', {
    className: 'btn btn--primary btn--sm',
    attrs: { type: 'button' },
    children: [iconDownload(14), createElement('span', { text: ' Importer' })],
    events: {
      click: (e) => {
        e.stopPropagation();
        openImportModal(tpl, () => loadTemplates(listContainer));
      },
    },
  });

  let deleteBtn = null;
  if (tpl.is_mine) {
    deleteBtn = createElement('button', {
      className: 'btn btn--ghost btn--sm community__delete-btn',
      attrs: { type: 'button', title: 'Supprimer mon template' },
      children: [iconTrash(14)],
      events: {
        click: (e) => {
          e.stopPropagation();
          openDeleteTemplateModal(tpl, listContainer);
        },
      },
    });
  }

  const card = createElement('div', {
    className: 'community__card',
    children: [
      createElement('div', {
        className: 'community__card-header',
        children: [
          createElement('div', {
            className: 'community__card-store',
            children: [
              iconStore(18),
              createElement('strong', { text: tpl.store.name }),
            ],
          }),
          createElement('span', {
            className: 'community__card-distance',
            children: [iconNavigation(12), createElement('span', { text: ` ${distText}` })],
          }),
        ],
      }),
      tpl.store.address ? createElement('div', {
        className: 'community__card-address',
        children: [iconMapPin(12), createElement('span', { text: ` ${tpl.store.address}` })],
      }) : null,
      createElement('div', {
        className: 'community__card-aisles',
        text: `${tpl.aisles.length} rayons : ${aisleNames}`,
      }),
      createElement('div', {
        className: 'community__card-footer',
        children: [
          createElement('span', {
            className: 'community__card-meta',
            text: `par ${tpl.pseudo}`,
          }),
          createElement('div', {
            className: 'community__card-actions',
            children: [deleteBtn, voteBtn, importBtn].filter(Boolean),
          }),
        ],
      }),
    ].filter(Boolean),
  });

  return card;
}

/**
 * Modale de confirmation de suppression d'un template.
 */
function openDeleteTemplateModal(tpl, listContainer) {
  const content = createElement('div', {
    children: [
      createElement('p', {
        text: `Voulez-vous vraiment supprimer votre template pour « ${tpl.store.name} » ?`,
      }),
      createElement('p', {
        className: 'text-muted mt-sm',
        attrs: { style: 'font-size: var(--font-size-sm)' },
        text: 'Cette action est irréversible. Les votes associés seront aussi supprimés.',
      }),
    ],
  });

  const deleteConfirmBtn = createElement('button', {
    className: 'btn btn--danger btn--block',
    attrs: { type: 'button' },
    text: 'Supprimer',
    events: {
      click: async () => {
        deleteConfirmBtn.disabled = true;
        deleteConfirmBtn.textContent = 'Suppression…';
        try {
          await deleteCommunityTemplate(tpl.id);
          closeModal();
          showToast('Template supprimé', 3000, 'success');
          await loadTemplates(listContainer);
        } catch (err) {
          deleteConfirmBtn.disabled = false;
          deleteConfirmBtn.textContent = 'Supprimer';
          showToast(err.message || 'Erreur', 3000, 'error');
        }
      },
    },
  });

  openModal({
    title: 'Supprimer ce template ?',
    content,
    footer: deleteConfirmBtn,
  });
}

/**
 * Calcule la distance en km entre deux points GPS.
 */
function getDistanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Modale de publication de template.
 */
async function openPublishModal(onSuccess) {
  const localStores = await getAllStores();

  if (localStores.length === 0) {
    showToast('Créez d\'abord un magasin avec des rayons', 3000, 'error');
    return;
  }

  // Vérifier que au moins un magasin a 2+ rayons
  const storesWithAisles = [];
  for (const store of localStores) {
    const aisles = await getAislesByStore(store.id);
    if (aisles.length >= 2) {
      storesWithAisles.push({ store, aisles });
    }
  }

  if (storesWithAisles.length === 0) {
    showToast('Il faut au moins 2 rayons dans un magasin pour publier', 3000, 'error');
    return;
  }

  const storeSelect = createElement('select', { className: 'input' });
  for (const { store } of storesWithAisles) {
    storeSelect.appendChild(createElement('option', {
      attrs: { value: String(store.id) },
      text: store.name,
    }));
  }

  const loggedIn = isLoggedIn();
  const currentUser = getUser();

  const pseudoInput = createElement('input', {
    className: 'input',
    attrs: {
      type: 'text',
      placeholder: loggedIn ? currentUser?.pseudo || 'Pseudo' : 'Votre pseudo (optionnel)',
      maxlength: '30',
      ...(loggedIn ? { disabled: 'true', value: currentUser?.pseudo || '' } : {}),
    },
  });
  if (loggedIn) pseudoInput.value = currentUser?.pseudo || '';

  // Localisation : saisie d'adresse uniquement (géocodage de l'adresse)
  let selectedLocation = null;

  const locationStatus = createElement('div', { className: 'location-status' });
  locationStatus.innerHTML = `<span class="location-status__icon">📍</span><span class="location-status__text">Saisissez l'adresse du magasin ci-dessous</span>`;

  const addressInput = createElement('input', {
    className: 'input',
    attrs: { type: 'text', placeholder: 'Ex : 12 rue de la Paix, Paris', autocomplete: 'off' },
  });

  const addressSuggestions = createElement('div', { className: 'address-suggestions hidden' });

  addressInput.addEventListener('input', async () => {
    const query = addressInput.value;
    const results = await searchAddressDebounced(query, { lat: userLat, lng: userLng });
    clearElement(addressSuggestions);

    if (results.length === 0) {
      addressSuggestions.classList.add('hidden');
      return;
    }

    addressSuggestions.classList.remove('hidden');
    for (const result of results) {
      addressSuggestions.appendChild(createElement('button', {
        className: 'address-suggestions__item',
        attrs: { type: 'button' },
        text: result.display_name,
        events: {
          click: () => {
            selectedLocation = { lat: result.lat, lng: result.lng, address: result.display_name };
            addressInput.value = result.display_name;
            addressSuggestions.classList.add('hidden');
            locationStatus.innerHTML = `<span class="location-status__icon">✅</span><span class="location-status__text">${result.display_name}</span>`;
          },
        },
      }));
    }
  });

  const addressSection = createElement('div', {
    className: 'address-autocomplete',
    children: [addressInput, addressSuggestions],
  });

  const locationWrapper = createElement('div', {
    className: 'location-wrapper',
    children: [addressSection, locationStatus],
  });

  // Prévisualisation des rayons
  const previewDiv = createElement('div', { className: 'community__publish-preview' });

  function updatePreview() {
    const selected = storesWithAisles.find(
      (s) => String(s.store.id) === storeSelect.value,
    );
    clearElement(previewDiv);
    if (selected) {
      previewDiv.appendChild(createElement('p', {
        className: 'text-muted',
        text: `${selected.aisles.length} rayons : ${selected.aisles.map((a) => a.name).join(', ')}`,
      }));
    }
  }

  storeSelect.addEventListener('change', updatePreview);
  updatePreview();

  const form = createElement('div', {
    className: 'add-article-form',
    children: [
      createElement('div', {
        className: 'input-group',
        children: [
          createElement('label', { className: 'input-group__label', text: 'Magasin à publier' }),
          storeSelect,
        ],
      }),
      createElement('div', {
        className: 'input-group',
        children: [
          createElement('label', { className: 'input-group__label', text: 'Localisation du magasin' }),
          locationWrapper,
        ],
      }),
      createElement('div', {
        className: 'input-group',
        children: [
          createElement('label', { className: 'input-group__label', text: 'Votre pseudo' }),
          pseudoInput,
        ],
      }),
      previewDiv,
    ],
  });

  const publishBtn = createElement('button', {
    className: 'btn btn--primary btn--block',
    attrs: { type: 'button' },
    text: 'Publier',
    events: {
      click: async () => {
        const selected = storesWithAisles.find(
          (s) => String(s.store.id) === storeSelect.value,
        );
        if (!selected) return;

        if (!selectedLocation) {
          showToast('Saisissez l\'adresse du magasin', 3000, 'error');
          return;
        }

        publishBtn.disabled = true;
        publishBtn.textContent = 'Publication…';

        try {
          await publishTemplate({
            store: {
              name: selected.store.name,
              address: selectedLocation.address,
              lat: selectedLocation.lat,
              lng: selectedLocation.lng,
            },
            pseudo: pseudoInput.value.trim() || 'Anonyme',
            device_id: getDeviceId(),
            aisles: selected.aisles.map((a) => ({ name: a.name, order: a.order })),
          });

          closeModal();
          showToast('Template publié ! 🎉', 3000, 'success');
          onSuccess();
        } catch (err) {
          publishBtn.disabled = false;
          publishBtn.textContent = 'Publier';
          showToast(err.message || 'Erreur', 3000, 'error');
        }
      },
    },
  });

  openModal({
    title: 'Publier un template',
    content: form,
    footer: publishBtn,
  });
}

/**
 * Modale d'import d'un template communautaire.
 */
function openImportModal(tpl, onSuccess) {
  const aislesList = tpl.aisles
    .sort((a, b) => a.order - b.order)
    .map((a) => `${a.order + 1}. ${a.name}`)
    .join('\n');

  const content = createElement('div', {
    children: [
      createElement('div', {
        className: 'community__import-info',
        children: [
          createElement('div', {
            className: 'community__import-store',
            children: [iconStore(20), createElement('strong', { text: ` ${tpl.store.name}` })],
          }),
          createElement('p', {
            className: 'text-muted',
            text: `par ${tpl.pseudo} • 👍 ${tpl.vote_count} votes`,
          }),
        ],
      }),
      createElement('div', {
        className: 'community__import-aisles',
        children: [
          createElement('p', {
            className: 'input-group__label',
            text: `${tpl.aisles.length} rayons :`,
          }),
          createElement('pre', {
            className: 'community__import-preview',
            text: aislesList,
          }),
        ],
      }),
      createElement('p', {
        className: 'text-muted',
        attrs: { style: 'margin-top: var(--space-md); font-size: var(--font-size-sm);' },
        text: 'Un nouveau magasin sera créé avec ces rayons. Vos données existantes ne seront pas modifiées.',
      }),
    ],
  });

  const importBtn = createElement('button', {
    className: 'btn btn--primary btn--block',
    attrs: { type: 'button' },
    text: 'Importer ce template',
    events: {
      click: async () => {
        importBtn.disabled = true;
        importBtn.textContent = 'Import en cours…';

        try {
          const storeId = await addStore(tpl.store.name);
          for (const aisle of tpl.aisles.sort((a, b) => a.order - b.order)) {
            await addAisle(storeId, aisle.name, { order: aisle.order });
          }

          closeModal();
          showToast('Template importé ! 🎉', 3000, 'success');
          navigate(`/store/${storeId}/settings`);
        } catch (err) {
          importBtn.disabled = false;
          importBtn.textContent = 'Importer ce template';
          showToast(err.message || 'Erreur', 3000, 'error');
        }
      },
    },
  });

  openModal({
    title: 'Importer ce template ?',
    content,
    footer: importBtn,
  });
}
